import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { Briefcase, Shield, BarChart3, Users, Zap, TrendingUp, Target, LineChart, Smartphone, ChevronLeft, ChevronRight, Dices, Goal, HeartHandshake, ShieldAlert, Headphones, Gift, Star, BadgeCheck } from 'lucide-react';

const ServicesSection = () => {
  const { t, language } = useLanguage();
  const [currentScreenshot, setCurrentScreenshot] = useState(0);
  const [currentConsultingImage, setCurrentConsultingImage] = useState(0);

  const consultingFeatures = t(translations.services.consulting.features);
  const betguardFeatures = t(translations.services.betguard.features);

  const consultingIcons = [Dices, Goal, HeartHandshake, ShieldAlert, Headphones, TrendingUp];
  const betguardIcons = [Star, Shield, Gift, BadgeCheck];

  const consultingImages = [
    {
      url: 'https://images.unsplash.com/photo-1760344594784-60ff14035eb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxmdXR1cmlzdGljJTIwdGVjaCUyMGFic3RyYWN0JTIwZ3JlZW4lMjBnbG93fGVufDB8fHx8MTc3NTY0MTQwN3ww&ixlib=rb-4.1.0&q=85',
      label: { en: 'Innovation', gr: 'Καινοτομία' }
    },
    {
      url: 'https://images.unsplash.com/photo-1768330187168-8acee7753983?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwzfHxmdXR1cmlzdGljJTIwdGVjaCUyMGFic3RyYWN0JTIwZ3JlZW4lMjBnbG93fGVufDB8fHx8MTc3NTY0MTQwN3ww&ixlib=rb-4.1.0&q=85',
      label: { en: 'Technology', gr: 'Τεχνολογία' }
    },
    {
      url: 'https://images.unsplash.com/photo-1762279389006-43963a0cee55?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGRpZ2l0YWwlMjBuZXR3b3JrJTIwZGF0YSUyMHZpc3VhbGl6YXRpb24lMjBkYXJrfGVufDB8fHx8MTc3NTY0MTQwN3ww&ixlib=rb-4.1.0&q=85',
      label: { en: 'Analytics', gr: 'Analytics' }
    }
  ];

  const appScreenshots = [
    {
      url: 'https://customer-assets.emergentagent.com/job_betguard-affiliate/artifacts/zdnoeq55_Screenshot%202026-04-06%20at%204.25.13%E2%80%AFPM.png',
      label: 'Home'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_betguard-affiliate/artifacts/ljmf7r4u_Screenshot%202026-04-06%20at%204.25.31%E2%80%AFPM.png',
      label: 'Operators'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_betguard-affiliate/artifacts/ppe30z7n_Screenshot%202026-04-06%20at%204.25.53%E2%80%AFPM.png',
      label: 'Safety Index'
    }
  ];

  const nextScreenshot = () => {
    setCurrentScreenshot((prev) => (prev + 1) % appScreenshots.length);
  };

  const prevScreenshot = () => {
    setCurrentScreenshot((prev) => (prev - 1 + appScreenshots.length) % appScreenshots.length);
  };

  const nextConsultingImage = () => {
    setCurrentConsultingImage((prev) => (prev + 1) % consultingImages.length);
  };

  const prevConsultingImage = () => {
    setCurrentConsultingImage((prev) => (prev - 1 + consultingImages.length) % consultingImages.length);
  };

  return (
    <section 
      id="services" 
      className="section-padding bg-[#050505] relative overflow-hidden"
      data-testid="services-section"
    >
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent" />
      <div className="absolute -left-40 top-1/2 w-80 h-80 bg-emerald-500/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
            data-testid="services-title"
          >
            {t(translations.services.sectionTitle)}
          </h2>
          <p className="text-base md:text-lg text-white/60 max-w-2xl mx-auto">
            {t(translations.services.sectionSubtitle)}
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Business Consulting Card */}
          <div 
            className="service-card group relative overflow-hidden"
            data-testid="service-consulting"
          >
            <div className="absolute top-0 right-0 w-40 h-40 bg-emerald-500/10 rounded-full blur-2xl group-hover:bg-emerald-500/20 transition-all duration-500" />
            
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-emerald-500" />
                </div>
                <h3 className="text-2xl font-bold text-white">
                  {t(translations.services.consulting.title)}
                </h3>
              </div>
              
              <p className="text-white/60 mb-8 leading-relaxed">
                {t(translations.services.consulting.description)}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {consultingFeatures.map((feature, index) => {
                  const Icon = consultingIcons[index];
                  return (
                    <div 
                      key={index}
                      className="flex items-center gap-3 p-3 rounded-lg bg-white/5 group-hover:bg-emerald-500/10 transition-colors"
                    >
                      <Icon className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      <span className="text-sm text-white/80">{feature}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* BetGuard Card */}
          <div 
            className="service-card group relative overflow-hidden"
            data-testid="service-betguard"
          >
            <div className="absolute top-0 right-0 w-40 h-40 bg-yellow-500/10 rounded-full blur-2xl group-hover:bg-yellow-500/20 transition-all duration-500" />
            
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                  <Smartphone className="w-6 h-6 text-yellow-500" />
                </div>
                <h3 className="text-2xl font-bold text-white">
                  {t(translations.services.betguard.title)}
                </h3>
              </div>
              
              <p className="text-white/60 mb-6 leading-relaxed">
                {t(translations.services.betguard.description)}
              </p>

              {/* App Screenshots Showcase */}
              <div className="mb-6 relative">
                <div className="flex items-center justify-center">
                  <button 
                    onClick={prevScreenshot}
                    className="absolute left-0 z-10 p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
                    data-testid="screenshot-prev"
                  >
                    <ChevronLeft className="w-5 h-5 text-white" />
                  </button>
                  
                  <div className="relative mx-12">
                    <div className="w-48 h-auto mx-auto rounded-2xl overflow-hidden shadow-2xl border border-white/10">
                      <img 
                        src={appScreenshots[currentScreenshot].url}
                        alt={`BetGuard App - ${appScreenshots[currentScreenshot].label}`}
                        className="w-full h-auto object-cover"
                        loading="lazy"
                      />
                    </div>
                    <p className="text-center text-sm text-white/50 mt-3">
                      {appScreenshots[currentScreenshot].label}
                    </p>
                  </div>

                  <button 
                    onClick={nextScreenshot}
                    className="absolute right-0 z-10 p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
                    data-testid="screenshot-next"
                  >
                    <ChevronRight className="w-5 h-5 text-white" />
                  </button>
                </div>

                {/* Dots indicator */}
                <div className="flex justify-center gap-2 mt-4">
                  {appScreenshots.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentScreenshot(index)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === currentScreenshot ? 'bg-yellow-500 w-4' : 'bg-white/30'
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* Key Highlights */}
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3 p-3 rounded-lg bg-gradient-to-r from-yellow-500/10 to-transparent border border-yellow-500/20">
                  <Users className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-white">{t(translations.services.betguard.highlights.community.title)}</h4>
                    <p className="text-xs text-white/50">{t(translations.services.betguard.highlights.community.description)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg bg-gradient-to-r from-emerald-500/10 to-transparent border border-emerald-500/20">
                  <Shield className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-white">{t(translations.services.betguard.highlights.safety.title)}</h4>
                    <p className="text-xs text-white/50">{t(translations.services.betguard.highlights.safety.description)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg bg-gradient-to-r from-purple-500/10 to-transparent border border-purple-500/20">
                  <Gift className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-white">{t(translations.services.betguard.highlights.offers.title)}</h4>
                    <p className="text-xs text-white/50">{t(translations.services.betguard.highlights.offers.description)}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {betguardFeatures.map((feature, index) => {
                  const Icon = betguardIcons[index];
                  return (
                    <div 
                      key={index}
                      className="flex items-center gap-2 p-2 rounded-lg bg-white/5 group-hover:bg-yellow-500/10 transition-colors"
                    >
                      <Icon className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                      <span className="text-xs text-white/80">{feature}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Client Logos Section */}
        <div className="mt-20 text-center">
          <p className="text-sm text-white/40 mb-8 uppercase tracking-widest">
            Trusted by Industry Leaders
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
            {['BetCorp', 'CasinoNova', 'ApexGaming', 'RoyalBet', 'WinMax'].map((name, index) => (
              <div 
                key={index}
                className="flex items-center gap-2 text-white/30 hover:text-white/60 transition-colors cursor-default"
              >
                <Shield className="w-5 h-5" />
                <span className="font-semibold tracking-tight">{name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
